import React, { useEffect, useRef } from "react";
import Quill from "quill";
import QuillBetterTable from "quill-better-table";
import "quill/dist/quill.snow.css"; // Quill.js styles
import "./style.css"; // Quill Better Table styles

// Register better-table module
Quill.register({
    'modules/better-table': QuillBetterTable,
}, true);

// Optional: Override Quill’s default table format to avoid conflict
const TableModule = Quill.import('formats/table');
TableModule.whitelist = ['better-table'];

const QuillEditor = ({ value, onChange }) => {
    const quillRef = useRef(null); // Ref for the Quill editor container
    const quillInstance = useRef(null); // Ref for the Quill instance
    const hasInitialized = useRef(false);

    // Helper: check if value is HTML
    const isHTML = (str) => /<\/?[a-z][\s\S]*>/i.test(str);

    // Initialize Quill.js when the component mounts
    useEffect(() => {
        if (quillRef.current && !quillInstance.current) {
            quillInstance.current = new Quill(quillRef.current, {
                theme: "snow", // Use the "snow" theme
                modules: {
                    toolbar: [
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        ["bold", "italic", "underline", "strike"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["link", "image"],
                        ["clean"],
                        ["table"]
                        // [{ 'table': 'better-table' }] // enable table button
                    ],
                    "better-table": {
                        operationMenu: {
                            items: {
                                insertColumnRight: true,
                                insertColumnLeft: true,
                                insertRowAbove: true,
                                insertRowBelow: true,
                                deleteRow: true,
                                deleteColumn: true,
                                mergeCells: true,
                                unmergeCells: true,
                            },
                            color: {
                                colors: ['red', 'green', 'blue', 'yellow', 'black'],
                                text: 'Background Colors:',
                            },
                        },
                    },
                    keyboard: {
                        bindings: QuillBetterTable.keyboardBindings
                    },
                },
            });

            // Only apply initial content once which is in plain text or html
            if (value && !hasInitialized.current) {
                if (isHTML(value)) {
                    quillInstance.current.clipboard.dangerouslyPasteHTML(value);
                } else {
                    quillInstance.current.setText(value);
                }
                hasInitialized.current = true;
            }

            // Listen for changes in the editor
            quillInstance.current.on("text-change", () => {
                const content = quillInstance.current.root.innerHTML;
                onChange(content); // Notify parent component of changes
            });
        }

        // Cleanup Quill instance when the component unmounts
        return () => {
            if (quillInstance.current) {
                quillInstance.current = null;
            }
        };
    }, []);

    // Update if value changes (post first edit)
    useEffect(() => {
        if (quillInstance.current && hasInitialized.current) {
            const currentHTML = quillInstance.current.root.innerHTML;
            if (currentHTML !== value && isHTML(value)) {
                quillInstance.current.clipboard.dangerouslyPasteHTML(value);
            }
        }
    }, [value]);

    useEffect(() => {
        if (quillInstance.current) {
            const toolbar = quillInstance.current.getModule("toolbar");
    
            toolbar.addHandler("table", () => {
                // Delay to ensure DOM is ready
                setTimeout(() => {
                    const tableModule = quillInstance.current.getModule("better-table");
                    tableModule.insertTable(3, 3);
                }, 50);
            });
        }
    }, []);

    return <div ref={quillRef} style={{ height: "800px", marginBottom: "20px" }} />;
};

export default QuillEditor;